"use client";

import React, { useEffect, useRef, useState } from 'react';
import Image from "next/image";
import {CircleArrowRight, X } from 'lucide-react';
import { useIntl } from "react-intl";

const DEFAULT_WHATSAPP_URL =
  "https://api.whatsapp.com/send?phone=919211367800&text=Hello,%20Please%20contact%20me%20regarding%20treatment%20options,%20Thank%20you!";

export default function WhatsappModal({
  isOpen,
  headline,
  onClose,
  whatsAppUrl = DEFAULT_WHATSAPP_URL,
}) {
  const intl = useIntl();

  const [phase, setPhase] = useState("dots");
  const timeoutRef = useRef(null);
  const readyTimeoutRef = useRef(null);

  useEffect(() => {

    if (!isOpen) {
      const resetTimeout = setTimeout(() => {
        setPhase("dots");
      }, 0);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (readyTimeoutRef.current) {
        clearTimeout(readyTimeoutRef.current);
        readyTimeoutRef.current = null;
      }
      return () => clearTimeout(resetTimeout);
    }

    const startTimeout = setTimeout(() => {
      setPhase("dots");
    }, 0);
    timeoutRef.current = setTimeout(() => {
      setPhase("preparing");
      readyTimeoutRef.current = setTimeout(() => {
        setPhase("ready");
      }, 1800);
    }, 1600);

    return () => {
      clearTimeout(startTimeout);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (readyTimeoutRef.current) clearTimeout(readyTimeoutRef.current);
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-[1200] flex items-center justify-center" onClick={onClose}>
      <div className="w-full max-w-md mx-4 sm:mx-0 bg-white rounded-lg p-4 sm:p-6 relative" onClick={(e)=>e.stopPropagation()}>

        <button
          onClick={onClose}
          aria-label="Close modal"
          className="absolute top-2 right-2 sm:top-3 sm:right-3 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          <span className="sr-only">Close</span>
          <X size={20} aria-hidden="true" />
        </button>

        <div className="flex flex-col gap-1">
          <h2 className="text-center text-[18px] sm:text-[19px] text-[#00428A] font-regular">
            {intl.formatMessage({
              id: "whatsappModal.headline",
              defaultMessage: "Getting you the guidance you need",
            })}
          </h2>


          <div className="relative h-8 sm:h-6 my-2" aria-live="polite" aria-atomic="true">

            <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${phase === "dots" ? "opacity-100" : "opacity-0"}`}>
              <span className="text-sm text-[#00428A] mr-2">
                {intl.formatMessage({
                  id: "whatsappModal.settingThingsUp",
                  defaultMessage: "Setting things up for you",
                })}
              </span>
              <span className="inline-block">
                <span className="w-2 h-2 bg-[#00428A] rounded-full mx-1 inline-block animate-bounce motion-reduce:animate-none" style={{ animationDelay: "0s" }} />
                <span className="w-2 h-2 bg-[#00428A] rounded-full mx-1 inline-block animate-bounce motion-reduce:animate-none" style={{ animationDelay: "0.12s" }} />
                <span className="w-2 h-2 bg-[#00428A] rounded-full mx-1 inline-block animate-bounce motion-reduce:animate-none" style={{ animationDelay: "0.24s" }} />
              </span>
            </div>

            <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${phase === "preparing" ? "opacity-100" : "opacity-0"}`}>
              <span className="text-sm text-[#00428A]">
                {intl.formatMessage({
                  id: "whatsappModal.preparingConversation",
                  defaultMessage: "Preparing your conversation",
                })}
              </span>
              <span className="inline-block">
                <span className="w-2 h-2 bg-[#00428A] rounded-full mx-1 inline-block animate-bounce motion-reduce:animate-none" style={{ animationDelay: "0s" }} />
                <span className="w-2 h-2 bg-[#00428A] rounded-full mx-1 inline-block animate-bounce motion-reduce:animate-none" style={{ animationDelay: "0.12s" }} />
                <span className="w-2 h-2 bg-[#00428A] rounded-full mx-1 inline-block animate-bounce motion-reduce:animate-none" style={{ animationDelay: "0.24s" }} />
              </span>
            </div>


            <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${phase === "ready" ? "opacity-100" : "opacity-0"}`}>

              <a
                href={whatsAppUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white text-sm px-6 py-2 rounded-full hover:brightness-95"
              >
                <CircleArrowRight size={16} aria-hidden="true" />
                <span>
                  {intl.formatMessage({
                    id: "whatsappModal.readyToConnect",
                    defaultMessage: "Ready to connect",
                  })}
                </span>
              </a>
            </div>
          </div>


          <div className="flex justify-center">
            <Image
              src="/images/Modal_CarePerson.svg"
              alt="Whatsapp Modal"
              width={180}
              height={120}
              className="w-40 sm:w-60 h-auto"
            />
          </div>
        </div>

        <div className="flex flex-col gap-4 text-center mt-4">
          <p className="text-[14px] sm:text-[16px] text-black-500 font-regular">
            {intl.formatMessage({
              id: "whatsappModal.thoughtfulGuidanceRealPeople",
              defaultMessage: "Thoughtful Guidance. Real People",
            })}
          </p>
        </div>
      </div>
    </div>
  );
};

